// Página de perfil de usuario
import { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import {
  User, Mail, Lock, Globe, Camera, Save, Key, Shield, Trophy, Coins, Star
} from 'lucide-react'
import useAuthStore from '../store/useAuthStore'
import { useToast } from '../components/ui/Toast'
import client from '../api/client'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
import Flag from '../components/ui/Flag'

const countries = [
  { code: 'bo', name: 'Bolivia' },
  { code: 'ar', name: 'Argentina' },
  { code: 'br', name: 'Brasil' },
  { code: 'cl', name: 'Chile' },
  { code: 'co', name: 'Colombia' },
  { code: 'ec', name: 'Ecuador' },
  { code: 'py', name: 'Paraguay' },
  { code: 'pe', name: 'Perú' },
  { code: 'uy', name: 'Uruguay' },
  { code: 've', name: 'Venezuela' },
  { code: 'us', name: 'Estados Unidos' },
  { code: 'mx', name: 'México' },
  { code: 'ca', name: 'Canadá' },
  { code: 'es', name: 'España' },
]

const getCountryCode = (countryName) => {
  const c = countries.find(item => item.name.toLowerCase() === countryName?.toLowerCase())
  return c ? c.code : null
}

export default function Profile() {
  const { user, login } = useAuthStore()
  const toast = useToast()
  
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [avatarFile, setAvatarFile] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputRef = useRef(null)

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      nombre: user?.nombre || '',
      apellido: user?.apellido || '',
      email: user?.email || '',
      pais: user?.pais || '',
      contrasena_actual: '',
      nueva_contrasena: '',
      confirmar_contrasena: '',
    }
  })

  // Sincronizar formulario si cambian los datos del usuario en el store
  useEffect(() => {
    if (user) {
      reset({
        nombre: user.nombre || '',
        apellido: user.apellido || '',
        email: user.email || '',
        pais: user.pais || '',
        contrasena_actual: '',
        nueva_contrasena: '',
        confirmar_contrasena: '',
      })
      setAvatarPreview(null)
      setAvatarFile(null)
    }
  }, [user, reset])

  const nuevaContrasena = watch('nueva_contrasena')

  // Obtener URL del avatar
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
  const avatarUrl = user?.avatar
    ? (user.avatar.startsWith('http') ? user.avatar : `${baseImgUrl}${user.avatar}`)
    : null

  const displayAvatar = avatarPreview || avatarUrl

  const getInitials = () => {
    const n = user?.nombre?.[0] || ''
    const a = user?.apellido?.[0] || ''
    return (n + a).toUpperCase() || 'U'
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen no debe exceder los 5MB')
        return
      }
      setAvatarFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  const onSubmit = async (data) => {
    setIsSubmitting(true)

    // Crear payload FormData
    const formData = new FormData()
    formData.append('nombre', data.nombre)
    formData.append('apellido', data.apellido)
    formData.append('email', data.email)
    formData.append('pais', data.pais)

    if (avatarFile) {
      formData.append('avatar', avatarFile)
    }

    if (data.nueva_contrasena) {
      formData.append('contrasena_actual', data.contrasena_actual)
      formData.append('nueva_contrasena', data.nueva_contrasena)
    }

    try {
      const response = await client.post('/auth/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data?.datos) {
        const { usuario, token } = response.data.datos
        // Guardar sesión actualizada
        login(usuario, token)
        toast.success('Tu perfil ha sido actualizado correctamente.', '¡Perfil Actualizado!')
      }
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'Error al actualizar el perfil.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const userCountryCode = getCountryCode(user?.pais)

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      {/* Encabezado */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-2"
      >
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary flex items-center gap-3">
          <User className="w-7 h-7 text-cyan" />
          Mi Perfil
        </h1>
        <p className="text-text-secondary mt-1">
          Gestiona tu información personal, avatar y opciones de seguridad.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna Izquierda: Tarjeta de Resumen */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-6"
        >
          <Card variant="default" padding="lg" className="text-center relative overflow-hidden" hover={false}>
            {/* Fondo decorativo */}
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-r from-cyan/20 to-indigo/20 blur-sm" />
            
            <div className="relative pt-6 flex flex-col items-center">
              {/* Contenedor del Avatar */}
              <div className="relative group mb-4">
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-cyan bg-surface-card flex items-center justify-center shadow-lg shadow-cyan/15 relative">
                  {displayAvatar ? (
                    <img
                      src={displayAvatar}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="font-display font-bold text-2xl text-cyan tracking-wider">
                      {getInitials()}
                    </span>
                  )}
                </div>
                {/* Botón de overlay */}
                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="absolute inset-0 rounded-full bg-navy-dark/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer border border-cyan/30"
                  title="Cambiar foto de perfil"
                >
                  <Camera className="w-6 h-6 text-cyan" />
                </button>
              </div>

              {/* Input de archivo oculto */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />

              <h2 className="font-display font-bold text-lg text-text-primary">
                {user?.nombre} {user?.apellido}
              </h2>
              
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-text-muted">{user?.email}</span>
              </div>

              <div className="flex items-center gap-1.5 mt-2 justify-center">
                {userCountryCode ? (
                  <div className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-navy-dark/40 border border-border-subtle text-xs text-text-secondary">
                    <Flag code={userCountryCode} className="w-3.5 h-2.5 object-cover rounded-sm shadow-sm" />
                    <span>{user?.pais}</span>
                  </div>
                ) : (
                  user?.pais && (
                    <span className="text-xs text-text-muted">({user.pais})</span>
                  )
                )}
                {user?.premium === 1 && (
                  <Badge variant="warning" size="sm" icon={Star}>Premium</Badge>
                )}
                {user?.rol === 'admin' && (
                  <Badge variant="cyan" size="sm" icon={Shield}>Administrador</Badge>
                )}
              </div>
            </div>

            <Card.Footer className="grid grid-cols-2 gap-4 text-center mt-6">
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wider font-semibold">Rol</p>
                <p className="text-sm font-semibold text-text-primary mt-0.5 capitalize">
                  {user?.rol || 'Usuario'}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wider font-semibold">Estado</p>
                <p className="text-sm font-semibold text-success mt-0.5 capitalize flex items-center justify-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-success shrink-0" />
                  {user?.estado || 'Activo'}
                </p>
              </div>
            </Card.Footer>
          </Card>
        </motion.div>

        {/* Columna Derecha: Formularios de Edición */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="lg:col-span-2 space-y-6"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Card 1: Datos Personales */}
            <Card variant="default" padding="lg" hover={false}>
              <Card.Header>
                <h3 className="font-display font-semibold text-text-primary flex items-center gap-2">
                  <User className="w-4 h-4 text-cyan" />
                  Datos Personales
                </h3>
              </Card.Header>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  type="text"
                  placeholder="Tu nombre"
                  error={errors.nombre?.message}
                  {...register('nombre', { required: 'El nombre es obligatorio' })}
                />

                <Input
                  label="Apellido"
                  type="text"
                  placeholder="Tu apellido"
                  error={errors.apellido?.message}
                  {...register('apellido', { required: 'El apellido es obligatorio' })}
                />

                <Input
                  label="Correo electrónico"
                  type="email"
                  placeholder="tu@correo.com"
                  error={errors.email?.message}
                  {...register('email', {
                    required: 'El correo electrónico es obligatorio',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Formato de correo inválido',
                    },
                  })}
                />

                <div className="flex flex-col gap-1.5">
                  <label className="font-body text-sm font-medium text-text-muted">País</label>
                  <select
                    className="w-full px-4 py-2.5 rounded-lg bg-surface-card border border-border-subtle text-text-primary text-sm focus:outline-none focus:border-cyan transition-colors"
                    {...register('pais')}
                  >
                    <option value="">Selecciona tu país</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>

            {/* Card 2: Cambio de Contraseña */}
            <Card variant="default" padding="lg" hover={false}>
              <Card.Header>
                <h3 className="font-display font-semibold text-text-primary flex items-center gap-2">
                  <Lock className="w-4 h-4 text-cyan" />
                  Opciones de Seguridad
                </h3>
                <Badge variant="default" size="sm">Opcional</Badge>
              </Card.Header>

              <div className="space-y-4">
                <p className="text-xs text-text-muted">
                  Completa esta sección únicamente si deseas cambiar tu contraseña de acceso actual.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Input
                    label="Contraseña actual"
                    type="password"
                    placeholder="••••••••"
                    error={errors.contrasena_actual?.message}
                    {...register('contrasena_actual', {
                      required: {
                        value: !!nuevaContrasena,
                        message: 'La contraseña actual es obligatoria para realizar el cambio.'
                      }
                    })}
                  />

                  <Input
                    label="Nueva contraseña"
                    type="password"
                    placeholder="Mín. 6 caracteres"
                    error={errors.nueva_contrasena?.message}
                    {...register('nueva_contrasena', {
                      minLength: {
                        value: 6,
                        message: 'La nueva contraseña debe tener al menos 6 caracteres'
                      }
                    })}
                  />

                  <Input
                    label="Confirmar nueva contraseña"
                    type="password"
                    placeholder="Repite la contraseña"
                    error={errors.confirmar_contrasena?.message}
                    {...register('confirmar_contrasena', {
                      validate: (val) => {
                        if (nuevaContrasena && val !== nuevaContrasena) {
                          return 'Las contraseñas no coinciden'
                        }
                        return true
                      }
                    })}
                  />
                </div>
              </div>
            </Card>

            {/* Acciones */}
            <div className="flex justify-end gap-3">
              <Button
                type="submit"
                loading={isSubmitting}
                icon={Save}
              >
                Guardar Cambios
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}
